import type { SelectorOption } from "./use-selector-roster";

/**
 * The selector filter box's option list, and what narrows it as someone types.
 *
 * Kept pure and out of `-components/selector-filter.tsx` for the same reason
 * `plans-model.ts` and `task-model.ts` are: the matching rule is the part
 * worth testing without rendering a react-aria-components tree, and pulling
 * it in here is what lets `selector-options.test.ts` do that.
 */

/**
 * Whether `option` matches what has been typed toward it so far.
 *
 * Matched on the label *or* the id, case-insensitively - a manager might have
 * either written down, and the requirement is "name or id" rather than just
 * one of them.
 *
 * Every option here is a person off the roster. The list used to carry a
 * synthesized "unassigned" entry as well, which had to be exempted from the id
 * half of this match because its id was a URL token nobody would type; the pool
 * is a status chip now, so the option list is the roster and nothing else.
 */
export function matchesSelectorQuery(
  option: SelectorOption,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }

  return (
    option.label.toLowerCase().includes(needle) ||
    option.id.toLowerCase().includes(needle)
  );
}

/** `options`, narrowed to what `query` matches - what the box actually renders. */
export function filterSelectorOptions(
  options: ReadonlyArray<SelectorOption>,
  query: string,
): SelectorOption[] {
  return options.filter((option) => matchesSelectorQuery(option, query));
}
